import React, { useEffect, useState } from "react";
import { ContractStoreModel, ACTION_TYPES } from "store/models/contracts";
import { createComponentStore } from "easy-peasy";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import {
  message,
  Spin,
  Col,
  Row,
  Input,
  Button,
  Select,
  Modal,
  InputNumber,
  Tooltip,
} from "antd";
import FormLayout, { FormSection, FormItem } from "Components/form";
import FetchSelect from "Components/fetch-select";
import { PartnerStoreModel } from "store/models/master-data/partner";
import DatePicker from "Components/date-picker-localized";
import RentableAreaList from "Components/form/rentablearea-list";
import moment from "moment";
import { TIME_OBJECTS } from "Constants/";

import { clean } from "Utils/";
import { ContractFormStoreModel } from "store/models/contracts/form";
import {
  validationSchema,
  conditionsValidationSchema,
} from "ValidationModels/contracts";

import ContractConditionList from "../conditions";
import CashFlow from "../cash-flow/index";
import CashFlowSummary from "../cash-flow/summary";
import Documents from "Components/form/documents-list";
import DocumentUploader from "Components/form/uploader";
import {
  DOCUMENT_REF_TYPE,
  DOCUMENT_STATUS,
  DOCUMENT_LANGUAGE,
} from "Constants/document";
import { RULES } from "Constants/rules";
import API from "API";
import { DISPLAY_MODE } from "Constants";
import PartnerSelect from "Components/partner-select";

const useContractStore = createComponentStore(ContractStoreModel);
const useContractFormStore = createComponentStore(ContractFormStoreModel);

export default function ContractForm({
  contract,
  mode = DISPLAY_MODE.READ_ONLY,
  loading = false,
  onRefresh,
  onAfterSuccess,
  ...props
}) {
  const [store, actions] = useContractStore();
  const [formStore, formActions] = useContractFormStore();

  const [summaryModalVisible, setSummaryModalVisible] = useState(false);

  const form = useForm({
    mode: "onSubmit",
    resolver: yupResolver(validationSchema),
    defaultValues: contract,
  });

  const {
    control,
    handleSubmit,
    errors,
    reset,
    setError,
    setValue,
    getValues,
    watch,
  } = form;

  const filesFieldArray = useFieldArray({
    control: control,
    name: "files",
    keyName: "key",
  });

  const { fields: files } = filesFieldArray;

  const watchFields = watch([
    "rentableAreas",
    "type",
    "dateOfFinish",
    "dateOfBeginning",
  ]);

  const contractType = watchFields.type;
  const contractDateOfFinish = watchFields.dateOfFinish;
  const contractDateOfBeginning = watchFields.dateOfBeginning;
  const rentableAreas = watchFields.rentableAreas;

  if (
    contractDateOfBeginning &&
    contractType?.rule?.code === RULES.RULE0001 &&
    rentableAreas?.length > 0 &&
    rentableAreas[0]?.rentableArea?.allocationType === "ORDER" &&
    !moment(contractDateOfFinish)
      .startOf("d")
      .isSame(moment(contractDateOfBeginning).add(5, "y").startOf("d"))
  ) {
    setValue("dateOfFinish", moment(contractDateOfBeginning).add(5, "y"));
  }

  const getPartnerTypeRuleCode4ContractType = (ruleCode) => {
    switch (ruleCode) {
      case "RULE0001": //lojman
        return "RULE0002";
      case "RULE0014": //kres
        return "RULE0003";
      default:
        return "RULE0017";
    }
  };

  const uploadDoc = async (ref) => {
    const formData = new FormData();
    let fileInfos = [];

    const values = getValues();

    if (ref && files.length > 0) {
      files.forEach((item, index) => {
        formData.append("files", item.file);

        let fileInfo = {
          referenceType: {
            id: DOCUMENT_REF_TYPE.contract,
          },
          referenceId: ref.id,
          documentName: values.files[index].documentName,
          releaseDate: moment(),
          status: { id: DOCUMENT_STATUS["Yayında"] },
          language: {
            id: DOCUMENT_LANGUAGE["Türkçe"],
          },
          revision: false,
        };
        fileInfos.push(fileInfo);
      });

      formData.append("json", JSON.stringify(fileInfos));
      const response = await API.post("/documents/uploadDocs", formData);
      return response;
    }
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      message.error("Sözleşme için girdiğiniz değerleri kontrol ediniz.");
    }
  }, [errors]);

  useEffect(() => {
    const resetForm = async () => {
      reset({ ...contract, files: [] });
      formActions.setConditions(contract.conditions);
    };

    resetForm();
  }, [contract]);

  useEffect(() => {
    switch (store.process) {
      case ACTION_TYPES.CREATED_DATA:
        message.success("Sözleşme oluşturuldu.");
        break;
      case ACTION_TYPES.UPDATED_DATA:
        message.success("Sözleşme güncellendi.");
        break;
      default:
        break;
    }
  }, [store.process]);

  const formOnSubmit = async (form) => {
    try {
      await conditionsValidationSchema.validate(formStore.conditions);
    } catch (error) {
      setError("conditions", {
        type: "manual",
        message: error.message,
      });
      return;
    }

    delete form.files;
    form.conditions = [...formStore.conditions];
    form.cashFlows = [...formStore.cashFlowOW];

    clean(form);

    try {
      if (mode === DISPLAY_MODE.EDIT) {
        let payload = {
          ...contract,
          ...form,
          conditions: form.conditions?.map((condition) => {
            if (condition.id)
              return {
                ...contract.conditions?.find((f) => f.id === condition.id),
                ...condition,
              };

            return condition;
          }),
          contractStatu: contract.contractStatu,
          rentableAreas: form.rentableAreas?.map((rentableArea) => {
            if (rentableArea.id)
              return {
                ...contract.rentableAreas?.find(
                  (f) => f.id === rentableArea.id
                ),
                ...rentableArea,
              };

            return rentableArea;
          }),
        };
        const response = await actions.update(payload);
        if (response) {
          await uploadDoc(response);
          onAfterSuccess(response);
        }
      } else {
        const response = await actions.create({
          ...contract,
          ...form,
          contractStatu: "ACTIVE",
        });
        if (response) {
          await uploadDoc(response);
          onAfterSuccess(response);
        }
      }
    } catch (error) {
      message.error(error.message || error);
    }
  };

  return (
    <Spin spinning={loading}>
      <form onSubmit={handleSubmit(formOnSubmit)} style={{ margin: "0 24px" }}>
        <FormLayout>
          <Col xs={24}>
            <FormSection title="Genel">
              <Row gutter={16}>
                {mode === DISPLAY_MODE.EDIT ||
                mode === DISPLAY_MODE.READ_ONLY ? (
                  <Col xs={24}>
                    <FormItem label="Kod" error={errors.code} required={true}>
                      <Controller
                        control={control}
                        name="code"
                        as={<Input disabled={true} />}
                      />
                    </FormItem>
                  </Col>
                ) : null}
                <Col xs={24} md={12}>
                  {contractType ? (
                    <FormItem
                      htmlFor="partner"
                      error={errors.partner}
                      label={
                        contractType?.rule?.code === RULES.RULE0014
                          ? "Kreş Öğrencisi"
                          : "Kiracı"
                      }
                      required={true}
                    >
                      <PartnerSelect
                        form={form}
                        field="partner"
                        model={PartnerStoreModel}
                        params={{
                          ruleCode: getPartnerTypeRuleCode4ContractType(
                            contractType?.rule?.code
                          ),
                        }}
                        defaultValue={contract?.partner}
                        style={{ width: "100%" }}
                        searchParam="fastSearchParam"
                        placeholder={
                          contractType?.rule?.code === RULES.RULE0014
                            ? "Öğrencisi seçiniz"
                            : "Kiracı seçiniz"
                        }
                      />
                    </FormItem>
                  ) : null}
                </Col>

                <Col xs={24} md={12}>
                  {contractType?.code === RULES.RULE0014 ? null : (
                    <FormItem
                      htmlFor="extensionRule"
                      error={errors.extensionRule?.id}
                      label="Sözleşme Uzatma Kuralı"
                    >
                      <FetchSelect
                        api="/masterdata/contractextensionrules"
                        form={form}
                        field="extensionRule"
                        placeholder="Sözleşme Uzatma Kuralı seçiniz"
                      />
                    </FormItem>
                  )}
                </Col>
                <Col xs={24} sm={12}>
                  <FormItem
                    label="Başlangıç Tarihi"
                    error={errors.dateOfBeginning}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="dateOfBeginning"
                      as={
                        <DatePicker
                          format="DD.MM.YYYY"
                          style={{ width: "100%" }}
                        />
                      }
                    />
                  </FormItem>
                </Col>
                <Col xs={24} sm={12}>
                  <Tooltip
                    title={
                      contractType?.rule?.code === RULES.RULE0001 &&
                      rentableAreas?.length > 0 &&
                      rentableAreas[0]?.rentableArea?.allocationType === "ORDER"
                        ? "Lojman türü sözleşmelerde kiralanabilir alanın tahsis türü sıra ise; sözleşme bitiş tarihi, başlangıç tarihinden 5 yıl sonradır."
                        : null
                    }
                  >
                    <FormItem
                      label="Bitiş Tarihi"
                      error={errors.dateOfFinish}
                      required={
                        !(
                          contractType?.rule?.code === RULES.RULE0001 &&
                          rentableAreas?.length > 0 &&
                          ["TASK", "SERVICE"].includes(
                            rentableAreas[0]?.rentableArea?.allocationType
                          )
                        )
                      }
                    >
                      <Controller
                        control={control}
                        name="dateOfFinish"
                        disabled={
                          contractType?.rule?.code === RULES.RULE0001 &&
                          rentableAreas?.length > 0 &&
                          rentableAreas[0]?.rentableArea?.allocationType ===
                            "ORDER"
                        }
                        as={
                          <DatePicker
                            format="DD.MM.YYYY"
                            style={{ width: "100%" }}
                          />
                        }
                      />
                    </FormItem>
                  </Tooltip>
                </Col>
                <Col xs={24}>
                  <FormItem
                    label="Açıklama"
                    error={errors.description}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="description"
                      as={<Input.TextArea rows={3} placeholder="Açıklama" />}
                    />
                  </FormItem>
                </Col>
                {contractType?.rule?.code === RULES.RULE0014 ? null : (
                  <>
                    <Col xs={24} sm={12}>
                      <FormItem
                        label="Yer Teslim Tarihi"
                        error={errors.dateOfPlaceDelivery}
                      >
                        <Controller
                          control={control}
                          name="dateOfPlaceDelivery"
                          as={
                            <DatePicker
                              format="DD.MM.YYYY"
                              style={{ width: "100%" }}
                            />
                          }
                        />
                      </FormItem>
                    </Col>
                    <Col xs={24} sm={12}>
                      <FormItem label="İmza Tarihi" error={errors.dateOfSign}>
                        <Controller
                          control={control}
                          name="dateOfSign"
                          as={
                            <DatePicker
                              format="DD.MM.YYYY"
                              style={{ width: "100%" }}
                            />
                          }
                        />
                      </FormItem>
                    </Col>
                  </>
                )}
              </Row>
            </FormSection>
            <FormSection title="Ekler">
              <DocumentUploader form={form} fieldArray={filesFieldArray} />
              <Documents
                documents={contract?.documents || []}
                mode={mode}
                onRemove={onRefresh}
              />
            </FormSection>

            {contractType?.rule?.code === RULES.RULE0014 ? null : (
              <FormSection
                title="Kiralanabilir Alanlar"
                error={errors.rentableAreas?.message || null}
                style={errors.rentableAreas ? { color: "red" } : null}
              >
                <Row gutter={16}>
                  <Col xs={24}>
                    <RentableAreaList
                      form={form}
                      singleMode={contractType?.rule?.code === RULES.RULE0001}
                      options={{ contractType: contractType }}
                    />
                  </Col>
                </Row>
              </FormSection>
            )}
            <FormSection
              title="Sözleşme Koşulları"
              style={errors.conditions ? { color: "red" } : null}
              error={errors.conditions?.message || null}
            >
              <Row gutter={16}>
                <Col xs={24}>
                  <ContractConditionList
                    conditions={formStore.conditions}
                    options={{
                      maxDateOfFinish: contractDateOfFinish,
                      maxDateOfBeginning: contractDateOfBeginning,
                      contractType: contractType,
                    }}
                    onAppend={(item) => {
                      formActions.appendCondition(item);
                    }}
                    onUpdate={(index, item) => {
                      formActions.updateCondition({
                        index: index,
                        item: item,
                      });
                    }}
                    onRemove={(index) => {
                      formActions.removeCondition(index);
                    }}
                  />
                </Col>
              </Row>
            </FormSection>
            <FormSection
              title="Nakit Akışı"
              style={errors.conditions ? { color: "red" } : null}
              error={
                errors.conditions
                  ? "Koşullardaki hataları gidermelisiniz."
                  : null
              }
              actions={[
                <Button
                  key="cashflow-summary"
                  onClick={() => setSummaryModalVisible(true)}
                >
                  Toplamlar
                </Button>,
              ]}
            >
              <CashFlow cashFlow={formStore.cashFlowOW} />
              <Modal
                footer={null}
                title="Nakit Akış Özeti"
                visible={summaryModalVisible}
                onCancel={() => setSummaryModalVisible(false)}
              >
                <CashFlowSummary cashflow={formStore.cashFlowOW} />
              </Modal>
            </FormSection>

            {contractType?.rule?.code === RULES.RULE0014 ||
            contractType?.rule?.code === RULES.RULE0001 ? null : (
              <FormSection title="Hatırlatıcı">
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <FormItem
                      htmlFor="notificationType"
                      error={errors.notificationType?.id}
                      label="Hatırlatma Şekli"
                    >
                      <FetchSelect
                        api="/masterdata/notificationtype"
                        form={form}
                        field="notificationType"
                        placeholder="Hatırlatma Şekli seçiniz"
                      />
                    </FormItem>
                  </Col>
                  <Col xs={24} md={4}>
                    <FormItem
                      label="Süresi"
                      error={errors.notificationDuration}
                    >
                      <Controller
                        control={control}
                        name="notificationDuration"
                        as={<InputNumber style={{ width: "100%" }} />}
                      />
                    </FormItem>
                  </Col>
                  <Col xs={24} md={4}>
                    <FormItem label="Zamanı" error={errors.timeObject}>
                      <Controller
                        control={control}
                        name="timeObject"
                        as={
                          <Select>
                            <Select.Option value="" key=""></Select.Option>
                            {Object.entries(TIME_OBJECTS).map((item) => (
                              <Select.Option value={item[0]} key={item[0]}>
                                {item[1]}
                              </Select.Option>
                            ))}
                          </Select>
                        }
                      />
                    </FormItem>
                  </Col>
                  <Col xs={24} md={8}>
                    <FormItem
                      htmlFor="mailGroup"
                      error={errors.mailGroup?.id}
                      label="E-Posta Grubu"
                    >
                      <FetchSelect
                        api="/masterdata/mailgroups"
                        form={form}
                        field="mailGroup"
                        placeholder="E-Posta Grubu seçiniz"
                      />
                    </FormItem>
                  </Col>
                </Row>
              </FormSection>
            )}
            <Row
              justify="space-between"
              style={{
                borderTop: "1px dashed rgb(166, 157, 152)",
                position: "sticky",
                bottom: 0,
                padding: "16px 0",
                backgroundColor: "#f2f2f2",
              }}
            >
              <Col>
                <Button
                  loading={
                    store.process === ACTION_TYPES.CREATING_DATA ||
                    store.process === ACTION_TYPES.UPDATING_DATA
                  }
                  style={{ width: 120, marginRight: 8 }}
                  type="primary"
                  htmlType="submit"
                >
                  Kaydet
                </Button>

                <Button
                  onClick={() => {
                    Modal.confirm({
                      title: "İptal",
                      content:
                        "Kaydetmediğiniz tüm değişiklikler, geri alınacaktır.",
                      okText: "Evet",
                      cancelText: "İptal",
                      onOk: () => {
                        reset();
                        formActions.setConditions(contract.conditions);
                      },
                    });
                  }}
                >
                  İptal
                </Button>
              </Col>
            </Row>
          </Col>
        </FormLayout>
      </form>
    </Spin>
  );
}
